import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import "./shared/styles/index.css";
import { verifyManifest } from "../integrations/tools/outlook";
import { config } from "./shared/config/environment";
import {
  createBeaconTicketManager,
  flushTelemetry,
  getTelemetryAuthToken,
  getTelemetryTokenRefresher,
  initTelemetry,
  logError,
  logEvent,
  scrubMessage,
} from "./telemetry";
import { ADDIN_VERSION } from "./telemetry/version";
import { registerCodeModeCommands } from "./integrations/deckorate/codeMode/commands";

Office.onReady((info) => {
  const bootStarted = performance.now();
  try {
    // -- Code Mode: associate the shared-runtime keyboard-shortcut handlers -
    // Unconditional and early, in every host. ExtendedOverrides is
    // manifest-scoped, so manifest-ppt-word.xml reserves the leader in Word as
    // well as PowerPoint; an Office action that is declared but never
    // associated is what produces "This add-in could not be started", so the
    // handler must exist everywhere the combination is reserved. It completes
    // without acting outside PowerPoint (see registerCodeModeCommands).
    // Isolated in its own try/catch: a failure here (e.g. an older Office.js
    // build without `Office.actions`) must not take down the whole boot -
    // the app should still render even if the slide-side shortcuts don't.
    try {
      registerCodeModeCommands();
    } catch (err) {
      logError("error.office-js", {
        type: err instanceof Error ? err.name : "CodeModeRegistrationError",
        scrubbedMessage: scrubMessage(err instanceof Error ? err.message : String(err)),
      });
    }

    // -- Boot the telemetry pipeline --------------------------------------
    // Wires the client, transport, and outbox. `installCaptureHooks` is on by
    // default so `window.error` and `unhandledrejection` flow straight into
    // the pipeline. `getBearerToken` reads the module-level token holder that
    // `AuthStateProvider` populates once sign-in settles - it returns null
    // until then, which makes the HTTP transport short-circuit (boot events
    // stay queued in the outbox and replay on the next flush after sign-in).
    // -- Beacon ticket: authenticates the pagehide sendBeacon flush -------
    // sendBeacon can't send an Authorization header, so the unload flush
    // carries a short-lived, HMAC-signed ticket instead. The manager fetches
    // one (once the bearer is available), caches it, and refreshes it before
    // it lapses; `getBeaconTicket` below reads that cached value synchronously
    // during pagehide. Until a ticket is cached the beacon path is a no-op and
    // events flush via the authenticated POST - no unsigned beacon is ever sent.
    const beaconTickets = createBeaconTicketManager({
      endpoint: `${config.telemetryApiBaseUrl}/api/telemetry/v1/beacon-ticket`,
      getBearerToken: async () => getTelemetryAuthToken(),
    });
    beaconTickets.start();

    initTelemetry({
      endpoint: `${config.telemetryApiBaseUrl}/api/telemetry/v1/events`,
      getBearerToken: async () => getTelemetryAuthToken(),
      // On a 401, defer to the refresher AuthStateProvider wires once auth
      // settles (a no-op until then), so a stale idToken is renewed and the
      // batch retried instead of dropped.
      refreshBearerToken: async () => {
        const refresher = getTelemetryTokenRefresher();
        return refresher ? refresher() : null;
      },
      // Cached beacon ticket for the pagehide sendBeacon path.
      getBeaconTicket: () => beaconTickets.current(),
      identity: {
        addInHost: hostName(info.host),
        addInVersion: ADDIN_VERSION,
        officeJsVersion: Office.context.diagnostics?.version ?? "unknown",
        platform: String(Office.context.diagnostics?.platform ?? "unknown"),
      },
    });
    logEvent("app.boot", "info");

    // -- Flush queued events when the user closes the document/task pane --
    // Use `globalThis` rather than `window` for portability - in a browser
    // they're the same object, but `globalThis` is the cross-environment name
    // recommended by ES2020 and our Sonar rules.
    globalThis.addEventListener("pagehide", () => {
      // elapsedMs on shutdown = session length (time since Office.onReady fired).
      // Populates the elapsedMs LAW column so ops can spot suspiciously short
      // sessions (auth loops, crashes) vs healthy day-long ones.
      logEvent("app.shutdown", "info", {
        elapsedMs: Math.round(performance.now() - bootStarted),
      });
      flushTelemetry("unload").catch(() => {
        /* swallow - flush already self-recovers */
      });
    });
    // -- And on network recovery, replay any rows that survived offline --
    globalThis.addEventListener("online", () => {
      flushTelemetry("reconnect").catch(() => {
        /* swallow - flush already self-recovers */
      });
    });

    // -- Render the React tree --------------------------------------------
    const root = createRoot(document.getElementById("root")!);
    root.render(
      <StrictMode>
        <App host={info.host} />
      </StrictMode>
    );

    // Time-to-first-paint: rAF fires once the browser has committed the
    // first frame after render. Two rAFs deep so the inner frame is the
    // one after layout has actually flushed (Chrome/Edge quirk; the first
    // rAF can land before paint).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        logEvent("perf.render.first-paint", "performance", {
          durationMs: Math.round(performance.now() - bootStarted),
        });
      });
    });

    // Fire-and-forget - logs (does not throw) when the Cosmos frontend-tool
    // registry has a handler the local build doesn't implement. Catches
    // deploy skew before users do.
    if (info.host === Office.HostType.Outlook) {
      void verifyManifest(config.aiApiBaseUrl, "outlook");
    }
  } catch (err) {
    // Any synchronous failure inside Office.onReady is by definition an
    // office-js initialisation problem - the host gave us bad info, the
    // diagnostics block was missing, or the DOM root wasn't found.
    logError("error.office-js", {
      type: err instanceof Error ? err.name : "OfficeOnReadyError",
      scrubbedMessage: scrubMessage(err instanceof Error ? err.message : String(err)),
    });
    throw err;
  }
});

function hostName(host: unknown): "outlook" | "word" | "powerpoint" {
  if (host === Office.HostType.Outlook) return "outlook";
  if (host === Office.HostType.Word) return "word";
  if (host === Office.HostType.PowerPoint) return "powerpoint";
  // Sensible default - main.tsx only renders on a real Office host.
  return "outlook";
}
